/**
 * NIC E-Way Bill API Error Code Map
 * Source: Official NIC E-Way Bill Specification + Whitebooks GSP
 */

export interface EWBErrorInfo {
    code: number;
    field: string;     // Target field for error context / form highlighting
    message: string;   // User-friendly description
    fix: string;       // Actionable resolution tip
    severity: 'error' | 'warning';
}

// ─── Unified Official NIC E-Way Bill Error Code Map ─────────────────────────
export const EWB_ERROR_MAP: Record<number, EWBErrorInfo> = {
    // ── Single-digit & General Errors ──────────────────────────────────────
    1: {
        code: 1, field: 'ewbNo', severity: 'error',
        message: 'Invalid E-Way Bill Number',
        fix: 'The E-Way Bill number is invalid or does not exist in the NIC portal.'
    },

    // ── Authentication & Token Errors ──────────────────────────────────────
    100: {
        code: 100, field: 'auth', severity: 'error',
        message: 'Invalid JSON payload',
        fix: 'Check the request payload format and structure.'
    },
    101: {
        code: 101, field: 'auth', severity: 'error',
        message: 'Invalid username',
        fix: 'Check EWB settings — username is incorrect.'
    },
    102: {
        code: 102, field: 'auth', severity: 'error',
        message: 'Invalid password',
        fix: 'Check EWB settings — password is incorrect.'
    },
    103: {
        code: 103, field: 'auth', severity: 'error',
        message: 'Invalid Client ID',
        fix: 'Check EWB settings — Client ID is incorrect.'
    },
    105: {
        code: 105, field: 'auth', severity: 'error',
        message: 'Invalid auth token',
        fix: 'Authentication token is invalid. Please re-authenticate.'
    },
    106: {
        code: 106, field: 'fromGstin', severity: 'error',
        message: 'Supplier GSTIN is invalid or token expired',
        fix: 'Check the supplier GSTIN in Company Profile or re-authenticate EWB settings.'
    },
    107: {
        code: 107, field: 'toGstin', severity: 'error',
        message: 'Customer GSTIN is invalid or authentication failed',
        fix: 'Check the customer GSTIN on the invoice. Use "URP" for unregistered customers.'
    },
    108: {
        code: 108, field: 'auth', severity: 'error',
        message: 'Username/password mismatch for the GSTIN state',
        fix: 'The login credentials do not match the GSTIN state. Check EWB settings.'
    },
    110: {
        code: 110, field: 'auth', severity: 'error',
        message: 'Invalid Client ID or Client Secret',
        fix: 'Check EWB settings — Client ID and Secret are incorrect.'
    },
    111: {
        code: 111, field: 'fromGstin', severity: 'error',
        message: 'GSTIN is not registered to this GSP',
        fix: 'Your GSTIN must be registered with Whitebooks GSP.'
    },
    238: {
        code: 238, field: 'auth', severity: 'error',
        message: 'Invalid authentication token',
        fix: 'Session expired. Please re-authenticate in EWB settings.'
    },

    // ── Document & Transaction Errors ──────────────────────────────────────
    201: {
        code: 201, field: 'supplyType', severity: 'error',
        message: 'Invalid supply type',
        fix: 'Supply type must be O (Outward) or I (Inward).'
    },
    202: {
        code: 202, field: 'subSupplyType', severity: 'error',
        message: 'Invalid sub-supply type',
        fix: 'Sub-supply type is invalid for the selected supply type and document type combination.'
    },
    203: {
        code: 203, field: 'subSupplyType', severity: 'error',
        message: 'Sub-transaction type does not match transaction type',
        fix: 'Select correct sub-supply type for the transaction (e.g. Supply=1 for Invoice).'
    },
    204: {
        code: 204, field: 'docType', severity: 'error',
        message: 'Invalid document type',
        fix: 'Use INV for invoice, CHL for delivery challan, BIL for bill of supply.'
    },
    205: {
        code: 205, field: 'docType', severity: 'error',
        message: 'Document type does not match transaction type',
        fix: 'Check document type matches supply and sub-supply type.'
    },
    206: {
        code: 206, field: 'docNo', severity: 'error',
        message: 'Invalid invoice/document number format',
        fix: 'Document number must be alphanumeric, max 16 characters.'
    },
    207: {
        code: 207, field: 'docDate', severity: 'error',
        message: 'Invalid invoice date',
        fix: 'Invoice date must be today or in the past (DD/MM/YYYY format).'
    },
    208: {
        code: 208, field: 'fromGstin', severity: 'error',
        message: 'Invalid supplier GSTIN or duplicate document number',
        fix: 'Check supplier GSTIN or ensure document number is unique.'
    },
    209: {
        code: 209, field: 'fromAddr1', severity: 'error',
        message: 'Blank supplier address',
        fix: 'Update company address in Company Profile.'
    },
    210: {
        code: 210, field: 'fromPincode', severity: 'error',
        message: 'Invalid or blank supplier pincode',
        fix: 'Update company address with correct 6-digit pincode.'
    },
    211: {
        code: 211, field: 'fromStateCode', severity: 'error',
        message: 'Invalid or blank supplier state code',
        fix: 'Update company profile with correct state code.'
    },

    // ── Consignee (To) & Address Errors ────────────────────────────────────
    212: {
        code: 212, field: 'toGstin', severity: 'error',
        message: 'Invalid customer/consignee GSTIN',
        fix: 'Check customer GSTIN on the invoice. Use URP for unregistered customers.'
    },
    213: {
        code: 213, field: 'toAddr1', severity: 'error',
        message: 'Invalid consignee address',
        fix: 'Check the delivery address on the invoice.'
    },
    214: {
        code: 214, field: 'toPincode', severity: 'error',
        message: 'Invalid consignee pincode',
        fix: 'Enter a valid 6-digit customer delivery pincode.'
    },
    215: {
        code: 215, field: 'toStateCode', severity: 'error',
        message: 'Invalid consignee state code',
        fix: 'Check the customer state code — must match delivery pincode.'
    },
    216: {
        code: 216, field: 'hsnCode', severity: 'error',
        message: 'Invalid HSN code',
        fix: 'HSN code must be minimum 6 digits (e.g. 8537 → 853710).'
    },
    217: {
        code: 217, field: 'qtyUnit', severity: 'error',
        message: 'Invalid unit quantity code (UQC)',
        fix: 'Use valid UQC codes: NOS, BOX, KGS, MTR, LTR etc.'
    },
    724: {
        code: 724, field: 'toPlace', severity: 'error',
        message: 'Destination place / city does not match customer state and pincode',
        fix: 'The destination place does not belong to the recipient state or pincode. Update Place / City in Customer Location.'
    },

    // ── Tax & Value Errors ──────────────────────────────────────────────────
    218: {
        code: 218, field: 'cgstRate', severity: 'error',
        message: 'Invalid tax rate for intrastate transaction',
        fix: 'For same-state supply, use CGST + SGST only. Remove IGST.'
    },
    219: {
        code: 219, field: 'igstRate', severity: 'error',
        message: 'Invalid tax rate for interstate transaction',
        fix: 'For different-state supply, use IGST only. Remove CGST/SGST.'
    },
    232: {
        code: 232, field: 'totInvValue', severity: 'error',
        message: 'Total invoice value mismatch',
        fix: 'Total invoice value must equal taxable amount + CGST + SGST + IGST + CESS (±₹2 allowed).'
    },
    251: {
        code: 251, field: 'cgstRate', severity: 'error',
        message: 'CGST and SGST tax rates must be equal',
        fix: 'CGST rate and SGST rate must be the same value.'
    },
    252: {
        code: 252, field: 'cgstRate', severity: 'error',
        message: 'Invalid CGST tax rate',
        fix: 'Use standard GST rates: 0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 9, 12, 14, 18, 28.'
    },
    253: {
        code: 253, field: 'sgstRate', severity: 'error',
        message: 'Invalid SGST tax rate',
        fix: 'Use standard GST rates: 0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 9, 12, 14, 18, 28.'
    },
    254: {
        code: 254, field: 'igstRate', severity: 'error',
        message: 'Invalid IGST tax rate',
        fix: 'Use standard GST rates: 0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 9, 12, 14, 18, 28.'
    },
    255: {
        code: 255, field: 'cessRate', severity: 'error',
        message: 'Invalid CESS rate',
        fix: 'Check CESS rate for the item category.'
    },
    263: {
        code: 263, field: 'totInvValue', severity: 'error',
        message: 'Total invoice value calculation mismatch',
        fix: 'Check item rate, quantity, and taxes. Total invoice value must match taxable + taxes.'
    },
    623: {
        code: 623, field: 'igstValue', severity: 'error',
        message: 'IGST value not applicable for intrastate transaction',
        fix: 'Same-state supply — use CGST + SGST only, set IGST to 0.'
    },
    624: {
        code: 624, field: 'cgstValue', severity: 'error',
        message: 'CGST/SGST not applicable for interstate transaction',
        fix: 'Different-state supply — use IGST only, set CGST/SGST to 0.'
    },

    // ── Distance & Pincode Errors ───────────────────────────────────────────
    221: {
        code: 221, field: 'transDistance', severity: 'error',
        message: 'Invalid approximate distance',
        fix: 'Enter a valid distance in km, or set to 0 for NIC auto-calculation.'
    },
    283: {
        code: 283, field: 'transDistance', severity: 'error',
        message: 'Distance between pincodes is too high or low',
        fix: 'Leave distance as 0 — NIC will auto-calculate from pincode database.'
    },
    436: {
        code: 436, field: 'toPincode', severity: 'error',
        message: 'Customer pincode does not match customer state',
        fix: 'Delivery pincode must match the customer state code.'
    },
    437: {
        code: 437, field: 'fromPincode', severity: 'error',
        message: 'Supplier pincode does not match supplier state',
        fix: 'Company address pincode must belong to your GSTIN state (check Company Profile).'
    },
    702: {
        code: 702, field: 'transDistance', severity: 'error',
        message: 'Distance between pincodes is too high or mismatch',
        fix: 'Leave distance as 0 — NIC will auto-calculate from pincode database.'
    },
    709: {
        code: 709, field: 'toPincode', severity: 'error',
        message: 'Pin-to-pin distance not available for given pincodes',
        fix: 'NIC does not have this pincode pair — enter distance manually.'
    },
    710: {
        code: 710, field: 'fromPincode', severity: 'error',
        message: 'Invalid state code for given pincode',
        fix: 'The pincode does not belong to the mentioned state.'
    },

    // ── Transport & Vehicle Errors ──────────────────────────────────────────
    220: {
        code: 220, field: 'transMode', severity: 'error',
        message: 'Invalid transport mode',
        fix: 'Transport mode must be 1=Road, 2=Rail, 3=Air, 4=Ship.'
    },
    222: {
        code: 222, field: 'transporterId', severity: 'error',
        message: 'Invalid transporter ID',
        fix: 'Transporter ID must be a valid 15-char GSTIN or enrolled TRANSIN.'
    },
    223: {
        code: 223, field: 'transDocNo', severity: 'error',
        message: 'Invalid transport document number',
        fix: 'Check the LR/RR/AWB number format.'
    },
    224: {
        code: 224, field: 'transDocDate', severity: 'error',
        message: 'Invalid transport document date',
        fix: 'Transport document date must be in DD/MM/YYYY format.'
    },
    225: {
        code: 225, field: 'vehicleNo', severity: 'error',
        message: 'Invalid vehicle number format',
        fix: 'Enter vehicle number without spaces (e.g. DL01AB1234).'
    },
    226: {
        code: 226, field: 'vehicleNo', severity: 'error',
        message: 'Both transport document and vehicle number are blank',
        fix: 'For road transport, enter vehicle number. For rail/air/ship, enter transport document number.'
    },
    248: {
        code: 248, field: 'vehicleNo', severity: 'error',
        message: 'Vehicle number required for extension in movement',
        fix: 'Vehicle number is mandatory when consignment status is Movement (M).'
    },
    303: {
        code: 303, field: 'vehicleNo', severity: 'error',
        message: 'Vehicle number is required for road transport',
        fix: 'Enter vehicle number when transport mode is Road.'
    },
    337: {
        code: 337, field: 'transporterId', severity: 'error',
        message: 'You cannot update transporter for this E-Way Bill',
        fix: 'The transporter cannot be modified at this stage.'
    },
    338: {
        code: 338, field: 'transporterId', severity: 'error',
        message: 'Transporter is already assigned',
        fix: 'Transporter details are already assigned and cannot be re-assigned.'
    },
    339: {
        code: 339, field: 'transporterId', severity: 'error',
        message: 'You are not assigned/authorized to update transporter details of this E-Way Bill',
        fix: 'Only the original generator of this E-Way Bill or the currently assigned transporter is authorized to update transporter details.'
    },
    340: {
        code: 340, field: 'transporterId', severity: 'error',
        message: 'Invalid Transporter ID or GSTIN',
        fix: 'Enter a valid 15-digit GSTIN or TRANSIN registered on the GST portal.'
    },
    341: {
        code: 341, field: 'transporterId', severity: 'error',
        message: 'Transporter ID cannot be same as supplier or customer GSTIN',
        fix: 'Enter a third-party transporter GSTIN or TRANSIN.'
    },
    361: {
        code: 361, field: 'vehicleType', severity: 'error',
        message: 'Invalid vehicle type',
        fix: 'Vehicle type must be R=Regular or O=Over Dimensional Cargo.'
    },
    362: {
        code: 362, field: 'transDocDate', severity: 'error',
        message: 'Transport document date cannot be before invoice date',
        fix: 'LR/AWB date must be on or after the invoice date.'
    },
    385: {
        code: 385, field: 'transDocNo', severity: 'error',
        message: 'Transport document date is mandatory for Rail/Ship/Air',
        fix: 'For non-road transport, transDocDate is required.'
    },
    501: {
        code: 501, field: 'vehicleNo', severity: 'error',
        message: 'Invalid vehicle number format',
        fix: 'Enter vehicle number without spaces (e.g. DL01AB1234). Must be a valid Indian registration.'
    },
    502: {
        code: 502, field: 'transporterId', severity: 'error',
        message: 'Invalid transporter ID / GSTIN',
        fix: 'Transporter ID must be a valid GSTIN or TRANSIN (enrolled transporter ID).'
    },
    504: {
        code: 504, field: 'transDocDate', severity: 'error',
        message: 'Transport document date must be on or after invoice date',
        fix: 'LR date / transport document date cannot be before the invoice date.'
    },
    560: {
        code: 560, field: 'transporterId', severity: 'error',
        message: 'Vehicle number or Transporter ID is mandatory',
        fix: 'For Road transport, provide either a Vehicle Number or Transporter ID.'
    },

    // ── Item Errors ─────────────────────────────────────────────────────────
    229: {
        code: 229, field: 'fromTrdName', severity: 'error',
        message: 'Supplier name is required',
        fix: 'Update company name in Company Profile.'
    },
    230: {
        code: 230, field: 'fromPlace', severity: 'error',
        message: 'Supplier place/city is required',
        fix: 'Update company address with city name.'
    },
    231: {
        code: 231, field: 'toTrdName', severity: 'error',
        message: 'Consignee/customer name is required',
        fix: 'Check customer name on the invoice.'
    },
    233: {
        code: 233, field: 'itemList', severity: 'error',
        message: 'EWB does not contain any items',
        fix: 'Add at least one item with HSN code, quantity and amount.'
    },
    234: {
        code: 234, field: 'taxableAmount', severity: 'error',
        message: 'Total amount / taxable amount is mandatory',
        fix: 'Set taxable amount for all items.'
    },
    310: {
        code: 310, field: 'hsnCode', severity: 'error',
        message: 'Invalid HSN code format',
        fix: 'HSN code must be at least 6 digits.'
    },
    401: {
        code: 401, field: 'itemList', severity: 'error',
        message: 'No items found in the invoice',
        fix: 'Add at least one item with HSN code, quantity and taxable amount.'
    },
    402: {
        code: 402, field: 'itemList', severity: 'error',
        message: 'Maximum 250 items allowed per E-Way Bill',
        fix: 'Split the invoice into multiple EWBs — each with up to 250 items.'
    },

    // ── Status, Duplicate, Cancel & Extension Errors ─────────────────────────
    280: {
        code: 280, field: 'ewbNo', severity: 'error',
        message: 'EWB status is not ACTIVE',
        fix: 'This EWB is already cancelled or expired.'
    },
    311: {
        code: 311, field: 'ewbNo', severity: 'error',
        message: 'E-Way Bill not found',
        fix: 'The EWB number does not exist. Check and try again.'
    },
    312: {
        code: 312, field: 'ewbNo', severity: 'error',
        message: 'E-Way Bill cannot be cancelled — already verified by officer',
        fix: 'Once goods are verified by a tax officer, the EWB cannot be cancelled.'
    },
    313: {
        code: 313, field: 'ewbNo', severity: 'error',
        message: 'E-Way Bill cannot be cancelled after 24 hours',
        fix: 'EWB cancellation is only allowed within 24 hours of generation.'
    },
    314: {
        code: 314, field: 'ewbNo', severity: 'error',
        message: 'E-Way Bill is already cancelled',
        fix: 'This EWB has already been cancelled. No further action needed.'
    },
    315: {
        code: 315, field: 'ewbNo', severity: 'error',
        message: 'Validity period lapsed — cannot cancel EWB',
        fix: 'EWB can only be cancelled within 24 hours of generation.'
    },
    316: {
        code: 316, field: 'ewbNo', severity: 'error',
        message: 'EWB is already verified — cannot cancel',
        fix: 'Once verified by tax officer, EWB cannot be cancelled.'
    },
    317: {
        code: 317, field: 'ewbNo', severity: 'error',
        message: 'Could not cancel EWB',
        fix: 'Contact EWB helpdesk with this error code.'
    },
    321: {
        code: 321, field: 'ewbNo', severity: 'error',
        message: 'E-Way Bill validity cannot be extended — already expired',
        fix: 'Validity can only be extended within 8 hours before or after expiry.'
    },
    322: {
        code: 322, field: 'ewbNo', severity: 'error',
        message: 'Extension not allowed — EWB validity extension limit reached',
        fix: 'E-Way Bill cannot be extended beyond 360 days from generation date.'
    },
    341: {
        code: 341, field: 'ewbNo', severity: 'error',
        message: 'You cannot reject an EWB generated by you',
        fix: 'Only the recipient can reject an EWB.'
    },
    342: {
        code: 342, field: 'ewbNo', severity: 'error',
        message: 'You are not the other party — cannot reject',
        fix: 'EWB can only be rejected by the consignee.'
    },
    343: {
        code: 343, field: 'ewbNo', severity: 'error',
        message: 'This EWB is already cancelled',
        fix: 'No further action needed — already cancelled.'
    },
    345: {
        code: 345, field: 'ewbNo', severity: 'error',
        message: 'Validity lapsed — cannot reject EWB',
        fix: 'EWB rejection must be done within 72 hours.'
    },
    346: {
        code: 346, field: 'ewbNo', severity: 'error',
        message: 'EWB can only be rejected within 72 hours',
        fix: 'Rejection window of 72 hours has passed.'
    },
    379: {
        code: 379, field: 'ewbNo', severity: 'error',
        message: 'EWB cannot be extended — already cancelled',
        fix: 'Cancelled EWBs cannot be extended.'
    },
    380: {
        code: 380, field: 'ewbNo', severity: 'error',
        message: 'EWB cannot be extended — not in active state',
        fix: 'Only ACTIVE EWBs can be extended.'
    },
    382: {
        code: 382, field: 'ewbNo', severity: 'error',
        message: 'Extension only allowed within 8 hours of expiry',
        fix: 'EWB can be extended only within 8 hours before or after its validity.'
    },
    389: {
        code: 389, field: 'remainingDistance', severity: 'error',
        message: 'Remaining distance is required for extension',
        fix: 'Enter the remaining distance to destination.'
    },
    390: {
        code: 390, field: 'remainingDistance', severity: 'error',
        message: 'Remaining distance cannot exceed actual distance',
        fix: 'Remaining distance must be less than the original distance.'
    },
    604: {
        code: 604, field: 'docNo', severity: 'error',
        message: 'EWB already generated for this document number',
        fix: 'An EWB already exists for this invoice. Cancel the old one first or use a different document number.'
    },
    652: {
        code: 652, field: 'validUntil', severity: 'error',
        message: 'Extension window closed — must extend within 8 hrs of expiry',
        fix: 'E-Way Bill validity can only be extended within 8 hours before or 8 hours after its expiry time.'
    },
    820: {
        code: 820, field: 'docDate', severity: 'error',
        message: 'Invoice date is older than 180 days',
        fix: 'E-Way Bill cannot be generated for invoices older than 180 days as per NIC rules.'
    },

    // ── GST Return Compliance Blocking ──────────────────────────────────────
    715: {
        code: 715, field: 'fromGstin', severity: 'error',
        message: 'Supplier GSTIN blocked — GST returns not filed',
        fix: 'Supplier has not filed GST returns for 2+ months. File pending returns first.'
    },
    716: {
        code: 716, field: 'toGstin', severity: 'error',
        message: 'Customer GSTIN blocked — GST returns not filed',
        fix: 'Customer has not filed GST returns for 2+ months.'
    },
    717: {
        code: 717, field: 'transporterId', severity: 'error',
        message: 'Transporter GSTIN blocked — GST returns not filed',
        fix: 'Transporter has not filed GST returns for 2+ months.'
    },
};

// ─── Parse error codes from Whitebooks API response ───────────────────────────
export function parseErrorCodes(errorMessage: string): number[] {
    try {
        const parsed = JSON.parse(errorMessage);
        const codesStr = parsed.errorCodes || parsed.errorCode || '';
        return codesStr
            .split(',')
            .map((c: string) => c.trim())
            .filter((c: string) => c !== '')
            .map(Number)
            .filter((n: number) => !isNaN(n));
    } catch {
        const matches = errorMessage.match(/\d{3,4}/g);
        return matches ? matches.map(Number) : [];
    }
}

export function getEWBErrorInfo(code: number): EWBErrorInfo {
    return EWB_ERROR_MAP[code] || {
        code,
        field: 'unknown',
        severity: 'error',
        message: `EWB error (code: ${code})`,
        fix: 'Please check all invoice and transport details and try again.',
    };
}

// ── JSON validation error field map ─────────────────────────────────────────
const JSON_FIELD_MAP: Record<string, { field: string; message: string; fix: string }> = {
  transporterId:     { field: 'transporterId',    message: 'Invalid Transporter ID format', fix: 'Must be a valid 15-character GSTIN (e.g. 05AAACG0904A1ZL) or leave completely empty.' },
  vehicleNo:         { field: 'vehicleNo',         message: 'Invalid vehicle number format', fix: 'Enter vehicle number without spaces (e.g. DL01AB1234) or leave empty.' },
  fromGstin:         { field: 'fromGstin',         message: 'Invalid supplier GSTIN format',  fix: 'GSTIN must be 15 characters (e.g. 29AAGCB1286Q000).' },
  toGstin:           { field: 'toGstin',           message: 'Invalid customer GSTIN format',  fix: 'GSTIN must be 15 characters or use URP for unregistered.' },
  fromPincode:       { field: 'fromPincode',       message: 'Invalid supplier pincode',       fix: 'Pincode must be a 6-digit number.' },
  toPincode:         { field: 'toPincode',         message: 'Invalid customer pincode',       fix: 'Pincode must be a 6-digit number.' },
  hsnCode:           { field: 'hsnCode',           message: 'Invalid HSN code',               fix: 'HSN code must be at least 6 digits.' },
  docNo:             { field: 'docNo',             message: 'Invalid document number',        fix: 'Document number max 16 characters, alphanumeric only.' },
  docDate:           { field: 'docDate',           message: 'Invalid invoice date format',    fix: 'Date must be in DD/MM/YYYY format.' },
  transDocDate:      { field: 'transDocDate',      message: 'Invalid transport document date', fix: 'Transport document date must be in DD/MM/YYYY format (e.g. 04/08/2026) or left empty.' },
  transDocNo:        { field: 'transDocNo',        message: 'Invalid transport document number', fix: 'LR/AWB document number must be alphanumeric or left empty.' },
  transDistance:     { field: 'transDistance',     message: 'Invalid distance value',         fix: 'Enter distance as a number (e.g. 150) or leave as 0 for auto-calculation.' },
  remainingDistance: { field: 'remainingDistance', message: 'Invalid remaining distance',    fix: 'Enter remaining distance to destination as a positive number.' },
  extnRsnCode:       { field: 'extnRsnCode',       message: 'Invalid extension reason code',  fix: 'Select a valid extension reason (e.g. 1=Natural Calamity).' },
  extnRemarks:       { field: 'extnRemarks',       message: 'Invalid extension remarks',      fix: 'Provide reason remarks for validity extension.' },
  fromPlace:         { field: 'fromPlace',         message: 'Invalid current place / city',   fix: 'Enter current place or city name.' },
  totInvValue:       { field: 'totInvValue',       message: 'Invalid total invoice value',    fix: 'Total value must equal taxable + taxes.' },
  taxableAmount:     { field: 'taxableAmount',     message: 'Invalid taxable amount',         fix: 'Taxable amount must be a positive number.' },
  transMode:         { field: 'transMode',         message: 'Invalid transport mode',         fix: 'Transport mode must be 1=Road, 2=Rail, 3=Air, 4=Ship.' },
  vehicleType:       { field: 'vehicleType',       message: 'Invalid vehicle type',           fix: 'Vehicle type must be R=Regular or O=Over Dimensional Cargo.' },
  fromStateCode:     { field: 'fromStateCode',     message: 'Invalid supplier state code',    fix: 'State code must be a valid 2-digit number (e.g. 07 for Delhi).' },
  toStateCode:       { field: 'toStateCode',       message: 'Invalid customer state code',    fix: 'State code must be a valid 2-digit number (e.g. 27 for Maharashtra).' },
  fromState:         { field: 'fromState',         message: 'Invalid state code',             fix: 'State code must be a valid 2-digit number (e.g. 29 for Karnataka).' },
};

function parseJsonValidationError(message: string) {
  if (!message.includes('JSON validation failed')) return null;

  const fieldMatch = message.match(/#\/([a-zA-Z]+)/);
  if (!fieldMatch) return null;

  const fieldName = fieldMatch[1];
  const info = JSON_FIELD_MAP[fieldName];

  if (info) return info;

  return {
    field: fieldName,
    message: `Invalid value for field: ${fieldName}`,
    fix: `Check the value entered for ${fieldName} and ensure it matches the required format.`,
  };
}

export function parseEWBErrors(errorDetails: any): {
    errors: EWBErrorInfo[];
    summary: string;
    toastMessage: string;
    fields: Record<string, string>;
} {
    const defaultResult = {
        errors: [] as EWBErrorInfo[],
        summary: typeof errorDetails === 'string' ? errorDetails : 'EWB operation failed. Please check details.',
        toastMessage: typeof errorDetails === 'string' ? errorDetails : 'EWB operation failed. Please check details.',
        fields: {} as Record<string, string>,
    };

    if (!errorDetails) return defaultResult;

    try {
        // ── Handle NIC/Whitebooks array format: { error: [{errorCode, errorMessage}], status_cd: "0" } ──
        const nicErrorArray = Array.isArray(errorDetails?.error) ? errorDetails.error : null;
        if (nicErrorArray && nicErrorArray.length > 0) {
            const codes = nicErrorArray
                .map((e: any) => Number(e.errorCode || e.ErrorCode || 0))
                .filter((n: number) => n > 0);

            const errors = codes.map((code: number) => getEWBErrorInfo(code));

            const fields: Record<string, string> = {};
            errors.forEach((e: EWBErrorInfo) => {
                if (e.field !== 'unknown') fields[e.field] = e.message;
            });

            const summary = errors.length === 1
                ? `${errors[0].message}. ${errors[0].fix}`
                : errors.map((e: EWBErrorInfo, i: number) => `${i + 1}. ${e.message}`).join('\n');

            return { errors, summary, toastMessage: summary, fields };
        }


        const errorMessage = typeof errorDetails === 'string' 
          ? errorDetails 
          : (errorDetails?.error?.message || errorDetails?.message || errorDetails?.status_desc || '');
        
        const infoBase64 = errorDetails?.error?.info || '';

        // Decode base64 info if present
        let infoText = '';
        if (infoBase64) {
            try {
                infoText = atob(infoBase64).replace(/^[\s,]+/, '').trim();
            } catch { /* ignore */ }
        }

        // Check for JSON validation error
        const jsonValidationError = parseJsonValidationError(errorMessage);
        if (jsonValidationError) {
          const fields = { [jsonValidationError.field]: jsonValidationError.message };
          const userMsg = `${jsonValidationError.message}. ${jsonValidationError.fix}`;
          return {
            errors: [{ code: 0, severity: 'error', ...jsonValidationError }],
            summary: userMsg,
            toastMessage: userMsg,
            fields,
          };
        }

        // Parse numeric error codes
        const codes = parseErrorCodes(errorMessage);
        const errors = codes.map(getEWBErrorInfo);

        // Build field map
        const fields: Record<string, string> = {};
        errors.forEach(e => {
            if (e.field !== 'unknown') fields[e.field] = e.message;
        });

        // Build summary message
        let summary = '';
        if (errors.length === 0) {
            summary = infoText || errorMessage || 'EWB operation failed. Please try again.';
        } else if (errors.length === 1) {
            summary = `${errors[0].message}. ${errors[0].fix}`;
        } else {
            summary = errors.map((e, i) => `${i + 1}. ${e.message}`).join('\n');
        }

        if (infoText && !summary.includes(infoText)) {
            summary += `\n\nDetails: ${infoText}`;
        }

        return { errors, summary, toastMessage: summary, fields };

    } catch {
        return defaultResult;
    }
}

export function getEWBErrorSummary(errorDetails: any): string {
    return parseEWBErrors(errorDetails).summary;
}